import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Preserve the full URL (path + search params) for redirect after login
    const returnUrl = location.pathname + location.search;
    const searchParams = new URLSearchParams(location.search);
    
    // Detect if this is a Shopify flow (from OAuth callback or onboarding)
    const isShopifyFlow = searchParams.has("shop") || 
                          searchParams.get("oauth_success") === "true" ||
                          location.pathname.includes("merchant");
    
    // Build login URL with platform hint for Shopify flows
    const loginParams = new URLSearchParams();
    loginParams.set("redirect", returnUrl);
    if (isShopifyFlow) {
      loginParams.set("platform", "shopify");
      // First-time Shopify merchants should see signup, not login
      loginParams.set("tab", "signup");
    }
    
    return <Navigate to={`/merchant-login?${loginParams.toString()}`} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
